<script setup lang="ts">
import videojs from "video.js";
import {
  defineProps,
  defineEmits,
  ref,
  onMounted,
  watch,
  nextTick,
  onUnmounted,
} from "vue";

import languagePack from "@/plugins/videojs/es.json";
import "@/plugins/videojs/nuevo.js";
import "@/plugins/videojs/videojs.css";

interface Props {
  src: string;
  isVisible: boolean;
}

const props = defineProps<Props>();
const emits = defineEmits(["close"]);
const isLoading = ref(false);
const isVideoReady = ref(false);

const videoPlayer = ref<HTMLVideoElement | null>(null);

watch(
  () => props.isVisible,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        isLoading.value = true;
        initVideoPlayer();
      });
    }
  },
);

const initVideoPlayer = () => {
  if (videoPlayer.value) {
    const videoOptions = {
      controls: true,
      autoplay: false,
      muted: false,
      sources: [{ src: props.src }],
    };

    const nuevoOptions = {
      textTrackSettings: false,
      settingsButton: false,
      fullscreenButton: false,
      pipButton: false,
      resOnly: true,
      theaterButton: false,
      rateMenu: true,
      shareMenu: false,
      zoomMenu: false,
      contextMenu: false,
      buttonRewind: false,
    };

    const player = videojs(videoPlayer.value, videoOptions, () => {
      videojs.addLanguage("es", languagePack);
      player.on("loadedmetadata", () => {
        isLoading.value = false;
        document.getElementById("fullVideo")?.classList.remove("hidden");
        player.play();
      });
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    player.nuevo(nuevoOptions);
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    closeVideo();
  }
};

const closeVideo = () => {
  emits("close");
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
  if (props.isVisible) {
    initVideoPlayer();
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
  <Transition name="modal">
    <div class="video-container" v-if="isVisible" @click.self="closeVideo">
      <button class="close-button" @click="$emit('close')">✕</button>
      <video
        id="fullVideo"
        :src="src"
        controls
        ref="videoPlayer"
        :class="['video-js', { hidden: !isVideoReady }]"
      />
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

.video-container {
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.hidden {
  display: none;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  border: none;
  background-color: vars.$off-white;
  color: vars.$off-black;
  font-size: 24px;
  border-radius: 0.25em;
  cursor: pointer;
  z-index: 99;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
